"use client";

import { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { RequestError, requestJson } from "@/lib/clientRequest";
import { toneBadge, toneDot, toneText } from "@/lib/statusTone";

export default function WorkerEnabledToggle({
  workerId,
  workerName = "worker",
  enabled,
  variant = "switch",
}: {
  workerId: string;
  workerName?: string;
  enabled: boolean;
  variant?: "switch" | "icon";
}) {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [error, setError] = useState<{ message: string; tone: "attention" | "danger" } | null>(null);
  // Show accepted changes immediately while the refreshed sidebar catches up.
  // New server props remain authoritative, including changes made elsewhere.
  const [confirmed, setConfirmed] = useState({ workerId, sourceEnabled: enabled, value: enabled });
  const changed = confirmed.workerId !== workerId || confirmed.sourceEnabled !== enabled;
  if (changed) setConfirmed({ workerId, sourceEnabled: enabled, value: enabled });
  const currentEnabled = changed ? enabled : confirmed.value;

  async function toggle() {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    const nextEnabled = !currentEnabled;
    try {
      await requestJson(`/api/workers/${workerId}`, {
        method: "PATCH",
        body: { enabled: nextEnabled },
        fallback: "Could not update the worker. Please retry.",
      });
      setConfirmed({ workerId, sourceEnabled: enabled, value: nextEnabled });
      router.refresh();
      window.dispatchEvent(
        new CustomEvent("caw:activity-refresh", { detail: { workerId } })
      );
    } catch (error) {
      setError({
        message: error instanceof RequestError ? error.message : "Could not update the worker. Check your connection.",
        tone: error instanceof RequestError && error.status === 409 ? "attention" : "danger",
      });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  const failure = error && (
    <div
      role="alert"
      className={`${variant === "icon"
        ? "order-last min-w-0 basis-full rounded-lg border p-2 text-xs break-words"
        : "absolute right-0 top-full z-30 mt-2 w-72 rounded-lg border bg-zinc-950 p-3 text-xs shadow-lg"
      } ${toneBadge[error.tone]}`}
    >
      {error.message}
      <button type="button" onClick={toggle} disabled={busy} className="ml-2 cursor-pointer underline disabled:opacity-60">Retry</button>
      <button type="button" onClick={() => setError(null)} className="ml-2 cursor-pointer underline">Dismiss</button>
    </div>
  );

  if (variant === "icon") {
    return (
      <>
        <button
          type="button"
          onClick={toggle}
          disabled={busy}
          aria-busy={busy}
          aria-label={`${currentEnabled ? "Pause" : "Resume"} ${workerName}`}
          title={currentEnabled
            ? "Enabled — Pause worker"
            : "Paused — Resume worker (enables triggers; does not start a run)"}
          className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-400 disabled:cursor-wait disabled:opacity-50"
        >
          <svg aria-hidden="true" viewBox="0 0 16 16" className="size-3.5" fill="currentColor">
            {currentEnabled
              ? <path d="M4 3h3v10H4zm5 0h3v10H9z" />
              : <path d="M5 2.75a.75.75 0 0 1 1.15-.64l7 4.5a.75.75 0 0 1 0 1.28l-7 4.5A.75.75 0 0 1 5 11.75z" />}
          </svg>
        </button>
        {failure}
      </>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        role="switch"
        aria-checked={currentEnabled}
        title={currentEnabled
          ? "Enabled — schedules, webhooks, and manual runs are live"
          : "Paused — triggers won't run (chat still works)"}
        className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition disabled:opacity-60 ${currentEnabled
          ? `${toneText.success} border-success/40 hover:border-success/60`
          : "border-zinc-700 text-zinc-400 hover:border-zinc-600"
        }`}
      >
        <span className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition ${currentEnabled ? toneDot.success : "bg-zinc-700"}`}>
          <span className={`inline-block size-3 transform rounded-full bg-white transition ${currentEnabled ? "translate-x-3.5" : "translate-x-0.5"}`} />
        </span>
        {currentEnabled ? "Enabled" : "Paused"}
      </button>
      {failure}
    </div>
  );
}
